import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import InfluencerProfileModal from './InfluencerProfileModal';
import InviteToBidModal from './InviteToBidModal';
import NotificationBell from './NotificationBell';
import Messages from './Messages';
import AccountStatusBanner from './AccountStatusBanner';
import SubmissionsPanel from './SubmissionsPanel';
import { MediaKitSummary, formatCount } from './MediaKit';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

function BrandDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [bids, setBids] = useState([]);
  const [creators, setCreators] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'jobs');
  const [showWelcome, setShowWelcome] = useState(searchParams.get('welcome') === '1');
  const profileMessageRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInfluencerId, setSelectedInfluencerId] = useState(null);
  const [badgeCounts, setBadgeCounts] = useState({
    pendingBids: 0,
    unreadMessages: 0,
    activeJobs: 0,
  });
  const [profileData, setProfileData] = useState({
    companyName: '',
    industry: '',
    website: '',
    bio: '',
    contactEmail: '',
    location: '',
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });
  const [creatorFilter, setCreatorFilter] = useState({ niche: '', minFollowers: '' });
  const [messageConversationId, setMessageConversationId] = useState(null);
  const [inviteInfluencer, setInviteInfluencer] = useState(null);
  // Content submissions waiting for review (drives the badge and per-job counts)
  const [reviewQueue, setReviewQueue] = useState([]);
  const [contentFilter, setContentFilter] = useState({ status: 'submitted', jobId: '' });

  const openConversation = (conversationId) => {
    setActiveTab('messages');
    if (conversationId) setMessageConversationId(conversationId);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!storedUser || !token) {
      navigate('/network');
      return;
    }

    const userData = JSON.parse(storedUser);
    if (userData.userType !== 'brand') {
      navigate('/network/influencer-dashboard');
      return;
    }

    setUser(userData);
    fetchJobs(token);
    fetchBids(token);
    fetchBadgeCounts(token);
    fetchReviewQueue(token);
    fetchCreators(token);
    fetchBrandProfile(token);

    // Keep badge counts fresh (new bids, unread messages, new content) while on the dashboard
    const interval = setInterval(() => {
      fetchBadgeCounts(token);
      fetchReviewQueue(token);
    }, 20000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchJobs = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/jobs/my-jobs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBids = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/bids/received`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setBids(data.bids || []);
      }
    } catch (err) {
      console.error('Error fetching bids:', err);
    }
  };

  const fetchBadgeCounts = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/badge-counts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setBadgeCounts(data.counts || {});
      }
    } catch (err) {
      console.error('Error fetching badge counts:', err);
    }
  };

  const fetchReviewQueue = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/submissions?status=submitted`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setReviewQueue(data.submissions || []);
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
    }
  };

  const openContentReview = (jobId = '') => {
    setContentFilter({ status: jobId ? '' : 'submitted', jobId });
    setActiveTab('content');
  };

  const handleAcceptBid = async (bidId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/bids/${bidId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        fetchBids(token);
        fetchJobs(token);
        fetchBadgeCounts(token);
        if (data.conversationId) {
          openConversation(data.conversationId);
        }
      }
    } catch (err) {
      console.error('Error accepting bid:', err);
    }
  };

  const handleRejectBid = async (bidId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/bids/${bidId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        fetchBids(token);
        fetchBadgeCounts(token);
      }
    } catch (err) {
      console.error('Error rejecting bid:', err);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job? This also removes its bids and conversations. This cannot be undone.')) {
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        fetchJobs(token);
        fetchBids(token);
        fetchBadgeCounts(token);
      }
    } catch (err) {
      console.error('Error deleting job:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/network');
  };

  const fetchCreators = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/creators`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setCreators(data.creators || []);
      }
    } catch (err) {
      console.error('Error fetching creators:', err);
    }
  };

  const fetchBrandProfile = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.user) {
        setProfileData({
          companyName: data.user.companyName || '',
          industry: data.user.industry || '',
          website: data.user.website || '',
          bio: data.user.bio || '',
          contactEmail: data.user.contactEmail || data.user.email || '',
          location: data.user.location || '',
        });
      }
    } catch (err) {
      console.error('Error fetching brand profile:', err);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setProfileMessage({ type: '', text: '' });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setIsSavingProfile(true);
    setProfileMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update profile');

      const storedUser = JSON.parse(localStorage.getItem('user'));
      const updatedUser = { ...storedUser, ...profileData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setProfileMessage({ type: 'error', text: err.message });
    } finally {
      setIsSavingProfile(false);
    }
  };

  useEffect(() => {
    if (profileMessage.text && profileMessageRef.current) {
      profileMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [profileMessage]);

  const filteredCreators = creators.filter(creator => {
    if (creatorFilter.niche && !creator.niches?.includes(creatorFilter.niche) && creator.niche !== creatorFilter.niche) {
      return false;
    }
    if (creatorFilter.minFollowers && (creator.followerCount || 0) < parseInt(creatorFilter.minFollowers)) {
      return false;
    }
    return true;
  });

  const formatFollowers = (count) => (count ? formatCount(count) : '0');

  const getStatusBadge = (status) => {
    const badges = {
      open: 'badge-open',
      in_progress: 'badge-progress',
      completed: 'badge-completed',
      closed: 'badge-closed',
    };
    return badges[status] || 'badge-open';
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loader"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src="/network-logo.png" alt="Alooma Network" className="sidebar-logo-img" />
          </Link>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
            My Jobs
            {badgeCounts.activeJobs > 0 && (
              <span className="nav-badge">{badgeCounts.activeJobs}</span>
            )}
          </button>
          <button
            className={`nav-item ${activeTab === 'creators' ? 'active' : ''}`}
            onClick={() => setActiveTab('creators')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Creator Database
          </button>
          <button
            className={`nav-item ${activeTab === 'bids' ? 'active' : ''}`}
            onClick={() => setActiveTab('bids')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            Bids Received
            {badgeCounts.pendingBids > 0 && (
              <span className="nav-badge highlight">{badgeCounts.pendingBids}</span>
            )}
          </button>
          <button
            className={`nav-item ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => openContentReview()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            Content to review
            {reviewQueue.length > 0 && (
              <span className="nav-badge highlight">{reviewQueue.length}</span>
            )}
          </button>
          <button
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Messages
            {badgeCounts.unreadMessages > 0 && (
              <span className="nav-badge highlight">{badgeCounts.unreadMessages}</span>
            )}
          </button>
          <button
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            My Profile
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.companyName?.charAt(0) || 'B'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.companyName}</span>
              <span className="user-type">Brand Account</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <h1>
              {activeTab === 'jobs' && 'My Job Postings'}
              {activeTab === 'creators' && 'Creator Database'}
              {activeTab === 'bids' && 'Bids Received'}
              {activeTab === 'content' && 'Content Submissions'}
              {activeTab === 'messages' && 'Messages'}
              {activeTab === 'profile' && 'My Profile'}
            </h1>
            <div className="header-actions">
              <NotificationBell onOpenConversation={openConversation} />
              {activeTab === 'jobs' && (
                <Link to="/network/post-job" className="create-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Post New Job
                </Link>
              )}
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          <AccountStatusBanner profileLink="/network/brand-dashboard?tab=profile" />
          {activeTab === 'jobs' && (
            <div className="jobs-grid">
              {jobs.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <h3>No jobs posted yet</h3>
                  <p>Create your first job posting to connect with talented creators.</p>
                  <Link to="/network/post-job" className="empty-cta">
                    Post Your First Job
                  </Link>
                </div>
              ) : (
                jobs.map((job) => (
                  <div key={job.id} className="job-card">
                    <div className="job-header">
                      <span className={`job-status ${getStatusBadge(job.status)}`}>
                        {job.status.replace('_', ' ')}
                      </span>
                      <span className="job-date">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="job-title">{job.title}</h3>
                    <p className="job-description">{job.description?.substring(0, 120)}...</p>
                    <div className="job-meta">
                      <div className="meta-item">
                        <span className="meta-label">Budget</span>
                        <span className="meta-value">${job.budget?.toLocaleString()}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Posts</span>
                        <span className="meta-value">{job.postCount}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Bids</span>
                        <span className="meta-value">{job.bidCount || 0}</span>
                      </div>
                    </div>
                    <div className="job-card-actions">
                      <Link to={`/network/job/${job.id}`} className="job-link">
                        View Details →
                      </Link>
                      <button className="job-review-link" onClick={() => openContentReview(job.id)}>
                        Content
                        {reviewQueue.some(sub => sub.jobId === job.id) &&
                          ` (${reviewQueue.filter(sub => sub.jobId === job.id).length} to review)`}
                      </button>
                      <div className="job-card-manage">
                        <button
                          className="job-manage-btn edit"
                          onClick={() => navigate(`/network/edit-job/${job.id}`)}
                          title="Edit job"
                        >
                          Edit
                        </button>
                        <button
                          className="job-manage-btn delete"
                          onClick={() => handleDeleteJob(job.id)}
                          title="Delete job"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'bids' && (
            <div className="bids-section">
              {bids.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📨</div>
                  <h3>No bids yet</h3>
                  <p>When creators bid on your jobs, they'll appear here.</p>
                </div>
              ) : (
                <div className="bids-list">
                  {bids.map((bid) => (
                    <div key={bid.id} className="bid-card received">
                      <div className="bid-header">
                        <div 
                          className="bid-creator-info clickable"
                          onClick={() => setSelectedInfluencerId(bid.influencerId)}
                          title="View creator profile"
                        >
                          <div className="creator-avatar">
                            {bid.influencerName?.charAt(0) || 'C'}
                          </div>
                          <div className="creator-details">
                            <h3 className="creator-name">{bid.influencerName}</h3>
                            {bid.influencerHandle && (
                              <span className="creator-handle">@{bid.influencerHandle}</span>
                            )}
                          </div>
                          <span className="view-profile-hint">View Profile →</span>
                        </div>
                        <span className={`bid-status status-${bid.status}`}>
                          {bid.status}
                        </span>
                      </div>
                      
                      <div className="bid-job-ref">
                        <span className="label">For:</span>
                        <Link to={`/network/job/${bid.jobId}`} className="job-ref-link">
                          {bid.jobTitle}
                        </Link>
                      </div>

                      <div className="bid-details">
                        <div className="bid-detail">
                          <span className="detail-label">Bid Amount</span>
                          <span className="detail-value amount">${bid.amount?.toLocaleString()}</span>
                        </div>
                        {bid.deliveryDays && (
                          <div className="bid-detail">
                            <span className="detail-label">Delivery</span>
                            <span className="detail-value">{bid.deliveryDays} days</span>
                          </div>
                        )}
                        {bid.followerCount > 0 && (
                          <div className="bid-detail">
                            <span className="detail-label">Followers</span>
                            <span className="detail-value">
                              {bid.followerCount >= 1000000 
                                ? (bid.followerCount / 1000000).toFixed(1) + 'M'
                                : bid.followerCount >= 1000 
                                  ? (bid.followerCount / 1000).toFixed(1) + 'K'
                                  : bid.followerCount}
                            </span>
                          </div>
                        )}
                      </div>

                      {bid.message && (
                        <div className="bid-message">
                          <span className="message-label">Message:</span>
                          <p>{bid.message}</p>
                        </div>
                      )}

                      <div className="bid-footer">
                        <span className="bid-date">
                          Received {new Date(bid.createdAt).toLocaleDateString()}
                        </span>
                        {bid.status === 'pending' && (
                          <div className="bid-actions">
                            <button 
                              className="action-btn reject"
                              onClick={() => handleRejectBid(bid.id)}
                            >
                              Decline
                            </button>
                            <button 
                              className="action-btn accept"
                              onClick={() => handleAcceptBid(bid.id)}
                            >
                              Accept Bid
                            </button>
                          </div>
                        )}
                        {bid.status === 'accepted' && (
                          <div className="bid-actions">
                            <span className="status-badge-inline accepted">✓ Accepted</span>
                            <button 
                              className="action-btn message"
                              onClick={() => openConversation(bid.conversationId)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                              </svg>
                              Message Creator
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'content' && (
            <div className="content-review-section">
              <div className="content-review-toolbar">
                <p>
                  Review the files, links and post captions creators submit for your campaigns, then
                  approve them or request changes.
                </p>
                <div className="filters-bar">
                  <select
                    className="filter-select"
                    value={contentFilter.status}
                    onChange={(e) => setContentFilter(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="submitted">To review ({reviewQueue.length})</option>
                    <option value="changes_requested">Changes requested</option>
                    <option value="approved">Approved</option>
                    <option value="">All submissions</option>
                  </select>
                  <select
                    className="filter-select"
                    value={contentFilter.jobId}
                    onChange={(e) => setContentFilter(prev => ({ ...prev, jobId: e.target.value }))}
                  >
                    <option value="">All campaigns</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>{job.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              <SubmissionsPanel
                role="brand"
                showCampaign={!contentFilter.jobId}
                query={{ status: contentFilter.status, jobId: contentFilter.jobId }}
                emptyText={contentFilter.status === 'submitted'
                  ? "You're all caught up. New content from creators will appear here."
                  : 'No submissions match these filters.'}
                onChanged={() => fetchReviewQueue(localStorage.getItem('token'))}
              />
            </div>
          )}

          {activeTab === 'messages' && (
            <Messages
              userType="brand"
              initialConversationId={messageConversationId}
              onActivity={() => fetchBadgeCounts(localStorage.getItem('token'))}
            />
          )}

          {activeTab === 'creators' && (
            <div className="creators-section">
              <div className="creators-intro">
                <p className="section-description">
                  Browse the Alooma talent roster. Find creators that match your brand, then invite them to submit an offer based on their listed rates or enter the bidding process for your campaigns.
                </p>
              </div>

              <div className="filters-bar">
                <select
                  className="filter-select"
                  value={creatorFilter.niche}
                  onChange={(e) => setCreatorFilter(prev => ({ ...prev, niche: e.target.value }))}
                >
                  <option value="">All Niches</option>
                  <option value="Food & Culinary">Food & Culinary</option>
                  <option value="Lifestyle">Lifestyle</option>
                  <option value="Fitness & Health">Fitness & Health</option>
                  <option value="Beauty & Fashion">Beauty & Fashion</option>
                  <option value="Travel">Travel</option>
                  <option value="Technology">Technology</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Gaming">Gaming</option>
                  <option value="Parenting & Family">Parenting & Family</option>
                  <option value="Sports">Sports</option>
                </select>
                <input
                  type="number"
                  className="filter-input"
                  placeholder="Min followers"
                  value={creatorFilter.minFollowers}
                  onChange={(e) => setCreatorFilter(prev => ({ ...prev, minFollowers: e.target.value }))}
                />
              </div>

              <div className="creators-grid">
                {filteredCreators.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3>No creators found</h3>
                    <p>Try adjusting your filters or check back as more creators join the platform.</p>
                  </div>
                ) : (
                  filteredCreators.map((creator) => (
                    <div key={creator.id} className="creator-card">
                      <div className="creator-card-header">
                        <div className="creator-avatar">
                          {creator.mediaKit?.headshotUrl ? (
                            <img src={creator.mediaKit.headshotUrl} alt="" />
                          ) : (
                            creator.fullName?.charAt(0) || 'C'
                          )}
                        </div>
                        <div className="creator-card-info">
                          <h3>{creator.fullName}</h3>
                          {creator.instagramHandle && (
                            <span className="creator-handle">@{creator.instagramHandle}</span>
                          )}
                        </div>
                      </div>

                      {creator.niches?.length > 0 && (
                        <div className="creator-niches">
                          {creator.niches.slice(0, 3).map(niche => (
                            <span key={niche} className="tag">{niche}</span>
                          ))}
                          {creator.niches.length > 3 && (
                            <span className="tag">+{creator.niches.length - 3}</span>
                          )}
                        </div>
                      )}

                      <div className="creator-stats">
                        <div className="stat">
                          <span className="stat-value">
                            {formatFollowers(creator.mediaKit?.totalFollowers ?? creator.followerCount)}
                          </span>
                          <span className="stat-label">{creator.mediaKit ? 'Total reach' : 'Followers'}</span>
                        </div>
                        {creator.rates?.length > 0 && (
                          <div className="stat">
                            <span className="stat-value">${Number(creator.rates[0].amount || 0).toLocaleString()}</span>
                            <span className="stat-label">{creator.rates[0].type?.replace('_', ' ')}</span>
                          </div>
                        )}
                      </div>

                      <MediaKitSummary mediaKit={creator.mediaKit} />

                      {creator.bio && (
                        <p className="creator-bio">{creator.bio.substring(0, 100)}{creator.bio.length > 100 ? '...' : ''}</p>
                      )}

                      <div className="creator-card-actions">
                        <button
                          className="action-btn-small"
                          onClick={() => setSelectedInfluencerId(creator.id)}
                        >
                          View Profile
                        </button>
                        <button
                          className="action-btn-small invite-btn"
                          onClick={() => setInviteInfluencer({ id: creator.id, name: creator.fullName })}
                        >
                          Invite to Bid
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="bidding-info-banner">
                <div className="banner-content">
                  <h3>How Alooma Bidding Works</h3>
                  <div className="bidding-benefits-row">
                    <div className="benefit-item">
                      <strong>For Brands:</strong> Post your campaign, set your budget, and let creators compete — you get cost efficiency, multiple options, and the best fit for your brand.
                    </div>
                    <div className="benefit-item">
                      <strong>For Creators:</strong> Access exclusive campaigns, submit competitive bids, and showcase your value — transparent budgets let you position yourself strategically.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="brand-profile-section">
              <div ref={profileMessageRef} />
              {showWelcome && (
                <div className="welcome-banner">
                  <h3>Welcome to Alooma Network!</h3>
                  <p>Complete your brand profile so creators know who they're working with. Add your company details below, then head to "My Jobs" to post your first campaign.</p>
                  <button className="welcome-dismiss" onClick={() => setShowWelcome(false)}>Got it</button>
                </div>
              )}
              <form onSubmit={handleProfileSubmit} className="profile-form">
                {profileMessage.text && (
                  <div className={`message ${profileMessage.type}`}>
                    {profileMessage.text}
                  </div>
                )}

                <section className="form-section">
                  <h2>Company Information</h2>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="companyName">Company Name</label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={profileData.companyName}
                        onChange={handleProfileChange}
                        placeholder="Your company name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="industry">Industry</label>
                      <select
                        id="industry"
                        name="industry"
                        value={profileData.industry}
                        onChange={handleProfileChange}
                      >
                        <option value="">Select industry</option>
                        <option value="food_beverage">Food & Beverage</option>
                        <option value="fashion">Fashion & Apparel</option>
                        <option value="beauty">Beauty & Personal Care</option>
                        <option value="technology">Technology</option>
                        <option value="health_fitness">Health & Fitness</option>
                        <option value="travel_hospitality">Travel & Hospitality</option>
                        <option value="entertainment">Entertainment</option>
                        <option value="automotive">Automotive</option>
                        <option value="finance">Finance & Banking</option>
                        <option value="retail">Retail & E-commerce</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        inputMode="url"
                        id="website"
                        name="website"
                        value={profileData.website}
                        onChange={handleProfileChange}
                        placeholder="yourcompany.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="location">Location</label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={profileData.location}
                        onChange={handleProfileChange}
                        placeholder="City, State"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="contactEmail">Contact Email</label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      value={profileData.contactEmail}
                      onChange={handleProfileChange}
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="bio">About Your Brand</label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={profileData.bio}
                      onChange={handleProfileChange}
                      placeholder="Tell creators about your brand, what you're looking for in partnerships, and your values..."
                      rows={4}
                    />
                  </div>
                </section>

                <div className="form-actions">
                  <button type="submit" className="save-btn" disabled={isSavingProfile}>
                    {isSavingProfile ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>

      {/* Influencer Profile Modal */}
      {selectedInfluencerId && (
        <InfluencerProfileModal
          influencerId={selectedInfluencerId}
          onClose={() => setSelectedInfluencerId(null)}
          onInvite={(id, name) => {
            setSelectedInfluencerId(null);
            setInviteInfluencer({ id, name });
          }}
        />
      )}

      {/* Invite to Bid Modal */}
      {inviteInfluencer && (
        <InviteToBidModal
          influencerId={inviteInfluencer.id}
          influencerName={inviteInfluencer.name}
          onClose={() => setInviteInfluencer(null)}
          onInvited={(conversationId) => {
            fetchBadgeCounts(localStorage.getItem('token'));
          }}
        />
      )}
    </div>
  );
}

export default BrandDashboard;

